// Phase 0088 — Scheduler + automation API client.
import apiClient from '@/shared/api/client'

export interface SchedulerJob {
  id: string
  job_code: string
  job_name: string
  description: string | null
  cron_expression: string | null
  interval_seconds: number | null
  handler_path: string
  enabled: boolean
  singleton: boolean
  last_run_at: string | null
  last_run_status: string | null
  last_run_duration_ms: number | null
  last_run_message: string | null
  next_run_at: string | null
  failure_count: number
  success_count: number
}

export interface SchedulerRunLog {
  id: string
  job_id: string
  job_code: string
  triggered_by: string
  triggered_by_user_id: string | null
  started_at: string | null
  finished_at: string | null
  status: string
  duration_ms: number | null
  rows_affected: number | null
  result_json: Record<string, unknown> | null
  error_text: string | null
}

export const schedulerApi = {
  list: async (): Promise<SchedulerJob[]> =>
    (await apiClient.get<SchedulerJob[]>('/scheduler/jobs')).data,
  runs: async (jobCode: string, limit = 50): Promise<SchedulerRunLog[]> =>
    (await apiClient.get<SchedulerRunLog[]>(
      `/scheduler/jobs/${jobCode}/runs`, { params: { limit } })).data,
  trigger: async (jobCode: string): Promise<Record<string, unknown>> =>
    (await apiClient.post(`/scheduler/jobs/${jobCode}/run`)).data,
  toggle: async (jobCode: string): Promise<SchedulerJob> =>
    (await apiClient.post(`/scheduler/jobs/${jobCode}/toggle`)).data,
}

export interface CertExpiryRow {
  skill_matrix_id: string
  employee_id: string
  emp_code: string
  emp_name: string
  department_id: string | null
  skill_name: string
  skill_category: string | null
  machine_type: string | null
  certified_from: string | null
  certified_till: string
  days_to_expiry: number
  status: string
  action_required: string
}

export interface AutomationDashboard {
  as_of: string
  kpis: {
    scheduler_jobs_active: number
    last_task_generation_run: {
      started_at: string | null
      status: string | null
      duration_ms: number | null
      rows_affected: number | null
    }
    last_escalation_sweep: {
      started_at: string | null
      status: string | null
      duration_ms: number | null
      rows_affected: number | null
    }
    open_escalations: number
    notifications_pending: number
    attendance_derivation_pending: number
    missing_punch_count_today: number
    task_templates_missing: number
    certifications_expiring_30d: number
    certifications_expired: number
    daily_scores_pending: number
    monthly_scores_pending: number
    study_tasks_generated: number
  }
  recent_failures: Array<{
    job_code: string
    started_at: string | null
    error_text: string | null
    duration_ms: number | null
  }>
}

export const hrAutomationApi = {
  deriveAttendance: async (body: {
    target_date?: string
    shift_id?: string
    employee_id?: string
  } = {}) =>
    (await apiClient.post<{
      target_date: string
      employees_processed: number
      rows_created: number
      rows_updated: number
      rows_flagged_review: number
      rows_skipped: number
    }>('/hr/attendance/derive-from-punches', body)).data,
  checkCertExpiry: async () =>
    (await apiClient.post('/hr/certifications/check-expiry')).data,
  certExpiryReport: async (withinDays = 60) =>
    (await apiClient.get<CertExpiryRow[]>('/hr/certifications/expiry-report',
      { params: { within_days: withinDays } })).data,
  generateTemplatesFromResponsibilities: async (roleId?: string) =>
    (await apiClient.post<{
      lines_examined: number
      templates_created: number
      skipped_existing: number
      skipped_unmapped_frequency: number
    }>('/hr/tasks/templates/generate-from-responsibilities',
      { role_id: roleId })).data,
  dashboard: async () =>
    (await apiClient.get<AutomationDashboard>('/hr/automation/dashboard')).data,
}
